import React, { useCallback, useEffect, useState } from 'react';
import { api, mutate } from '../../shared/api';
import { formatDateTime } from '../../shared/formatting';
import type { User } from '../../types/biApp';

type Locale = 'hr' | 'en';

const BROWN = 'var(--ad-ink)';
const GOLD = 'var(--ad-acc)';

const card: React.CSSProperties = { background: '#fff', borderRadius: 14, padding: 18, boxShadow: '0 2px 10px rgba(107,79,28,0.10)', border: '1px solid #efe6d2' };
const tabBtn = (active: boolean): React.CSSProperties => ({
    padding: '8px 16px', borderRadius: 999, border: '1px solid ' + (active ? BROWN : '#e0d6c0'),
    background: active ? BROWN : '#fff', color: active ? '#fff' : BROWN, cursor: 'pointer', fontWeight: 600, fontSize: '0.86rem',
});
const inp: React.CSSProperties = { padding: '8px 10px', borderRadius: 8, border: '1px solid #d8cdb2', fontSize: '0.86rem', width: '100%', boxSizing: 'border-box' };
const th: React.CSSProperties = { textAlign: 'left', padding: '8px 10px', fontSize: '0.74rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: BROWN, borderBottom: '2px solid #efe6d2' };
const td: React.CSSProperties = { padding: '8px 10px', fontSize: '0.86rem', borderBottom: '1px solid #f3ecdb' };

type TicketRow = {
    id: number;
    public_id: string;
    department_id: number | null;
    employee_id: number | null;
    subject: string;
    body: string | null;
    item_type: string;
    priority: string;
    status: string;
    assigned_to: number | null;
    payment_request_id: number | null;
    created_by: number | null;
    created_at: string;
    updated_at: string;
    department_name: string | null;
    assigned_to_name: string | null;
    created_by_name: string | null;
    employee_name: string | null;
};

type Comment = { id: number; user_id: number | null; body: string; source?: string; author_email?: string | null; created_at: string; user_name: string | null };

const ITEM_TYPES: { value: string; hr: string; en: string }[] = [
    { value: 'racunalo', hr: 'Računalo', en: 'Computer' },
    { value: 'mobitel', hr: 'Mobitel', en: 'Phone' },
    { value: 'sustav', hr: 'Sustav / softver', en: 'System / software' },
    { value: 'ostalo', hr: 'Ostalo', en: 'Other' },
];

const PRIORITIES: { value: string; hr: string; en: string; color: string }[] = [
    { value: 'p1', hr: 'P1 kritično', en: 'P1 critical', color: '#b14040' },
    { value: 'p2', hr: 'P2 visoko', en: 'P2 high', color: '#cf7a1e' },
    { value: 'p3', hr: 'P3 normalno', en: 'P3 normal', color: '#a6791f' },
    { value: 'p4', hr: 'P4 nisko', en: 'P4 low', color: '#7a9a5a' },
];

const STATUSES: { value: string; hr: string; en: string; color: string }[] = [
    { value: 'open', hr: 'Otvoren', en: 'Open', color: '#a6791f' },
    { value: 'in_progress', hr: 'U obradi', en: 'In progress', color: '#3a6fb0' },
    { value: 'waiting_customer', hr: 'Čeka korisnika', en: 'Waiting for customer', color: '#8b6fb0' },
    { value: 'resolved', hr: 'Riješen', en: 'Resolved', color: '#2f8f4e' },
    { value: 'closed', hr: 'Zatvoren', en: 'Closed', color: '#5a5a5a' },
];

function lookup(list: { value: string; hr: string; en: string }[], v: string, locale: Locale): string {
    const o = list.find((x) => x.value === v);
    return o ? (locale === 'en' ? o.en : o.hr) : v;
}

function Badge({ list, value, locale }: { list: { value: string; hr: string; en: string; color: string }[]; value: string; locale: Locale }) {
    const o = list.find((x) => x.value === value);
    const color = o?.color ?? '#777';
    return <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: '0.72rem', fontWeight: 700, color: '#fff', background: color }}>{o ? (locale === 'en' ? o.en : o.hr) : value}</span>;
}

export function TicketsTab({ token, locale, user, basePath = '/hr/tickets', canManageOverride }: { token: string; locale: Locale; user: User; basePath?: string; canManageOverride?: boolean }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const isFull = canManageOverride ?? (user.role === 'superadministrator' || user.hr_access_level === 'hr_manager');

    const [rows, setRows] = useState<TicketRow[] | null>(null);
    const [err, setErr] = useState<string | null>(null);
    const [showForm, setShowForm] = useState(false);
    const [openId, setOpenId] = useState<number | null>(null);
    const [fullscreen, setFullscreen] = useState(false);
    const [query, setQuery] = useState('');

    const load = useCallback(() => {
        setRows(null);
        setErr(null);
        api<{ tickets: TicketRow[] }>(basePath, token)
            .then((r) => setRows(r.tickets))
            .catch((e) => { setErr(String(e?.message ?? e)); setRows([]); });
    }, [token, basePath]);
    useEffect(load, [load]);

    const openRow = rows?.find((r) => r.id === openId) ?? null;
    const visible = (rows ?? []).filter((r) => !query.trim() || `${r.subject} ${r.public_id} ${r.department_name ?? ''}`.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase()));
    const moveTicket = async (id: number, status: string) => {
        if (!isFull) return;
        const before = rows;
        setRows((current) => current?.map((r) => r.id === id ? { ...r, status } : r) ?? null);
        try { await mutate(`${basePath}/${id}`, token, 'PATCH', { status }); }
        catch (e) { setRows(before); setErr(String((e as Error)?.message ?? e)); }
    };

    const columns = STATUSES.filter((s) => s.value !== 'closed');

    return (
        <div style={fullscreen ? { position: 'fixed', inset: 0, zIndex: 8000, background: '#f6f1e7', padding: 18, overflow: 'auto' } : card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
                <div><strong style={{ color: BROWN, fontSize: '1.05rem' }}>{t('Ticketing', 'Ticketing')} {rows ? `(${rows.length})` : ''}</strong>
                    <div className="muted" style={{ fontSize: '0.73rem', marginTop: 2 }}>ticket@aurodomus.eu · bis@aurodomus.org</div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                    <input aria-label={t('Pretraži tikete', 'Search tickets')} style={{ ...inp, width: 220 }} value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('Pretraži…', 'Search…')} />
                    <button type="button" style={tabBtn(false)} onClick={() => setFullscreen((v) => !v)}>{fullscreen ? '↙ ' + t('Izađi', 'Exit') : '⛶ ' + t('Cijeli ekran', 'Fullscreen')}</button>
                    <button type="button" style={tabBtn(true)} onClick={() => setShowForm((v) => !v)}>＋ {t('Novi tiket', 'New ticket')}</button>
                </div>
            </div>

            {showForm ? <NewTicketForm token={token} locale={locale} isFull={isFull} basePath={basePath} onDone={() => { setShowForm(false); load(); }} /> : null}

            {err ? <div style={{ color: '#b14040', marginBottom: 8 }}>{err}</div> : null}
            {rows === null ? <div className="muted">{t('Učitavanje…', 'Loading…')}</div>
                : visible.length === 0 ? <div className="muted">{t('Nema tiketa.', 'No tickets.')}</div>
                : (
                    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${columns.length}, minmax(230px, 1fr))`, gap: 10, overflowX: 'auto', minHeight: fullscreen ? 'calc(100vh - 120px)' : 460, alignItems: 'stretch' }}>
                        {columns.map((column) => {
                            const columnRows = visible.filter((r) => column.value === 'resolved' ? ['resolved', 'closed'].includes(r.status) : r.status === column.value);
                            return <section key={column.value} onDragOver={(e) => { if (isFull) e.preventDefault(); }} onDrop={(e) => moveTicket(Number(e.dataTransfer.getData('text/ticket-id')), column.value)} style={{ minWidth: 230, background: '#f4efe4', borderRadius: 12, padding: 9, borderTop: `4px solid ${column.color}` }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', color: BROWN, fontWeight: 700, fontSize: '0.82rem', padding: '2px 3px 9px' }}><span>{locale === 'en' ? column.en : column.hr}</span><span>{columnRows.length}</span></div>
                                {columnRows.map((r) => <button key={r.id} type="button" draggable={isFull} onDragStart={(e) => e.dataTransfer.setData('text/ticket-id', String(r.id))} onClick={() => setOpenId(r.id)} style={{ display: 'block', width: '100%', textAlign: 'left', border: '1px solid #e4d9c1', borderRadius: 10, background: '#fff', padding: 11, marginBottom: 8, cursor: 'pointer', boxShadow: '0 2px 6px rgba(70,50,20,.07)' }}>
                                    <div style={{ fontWeight: 700, color: '#3f321b', lineHeight: 1.3 }}>{r.subject}</div>
                                    <div style={{ color: '#8b7a5d', fontSize: '0.69rem', margin: '5px 0 8px' }}>TKT-{r.public_id} · {r.department_name ?? t('Bez poslovnice', 'No branch')}</div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 4 }}><Badge list={PRIORITIES} value={r.priority} locale={locale} /><span style={{ color: '#85775f', fontSize: '0.68rem' }}>{r.assigned_to_name ?? t('Nedodijeljen', 'Unassigned')}</span></div>
                                </button>)}
                            </section>;
                        })}
                    </div>
                )}

            {openRow ? <TicketDetail ticket={openRow} token={token} locale={locale} canManage={isFull} basePath={basePath} onClose={() => setOpenId(null)} onChanged={load} /> : null}
        </div>
    );
}

function NewTicketForm({ token, locale, isFull, basePath, onDone }: { token: string; locale: Locale; isFull: boolean; basePath: string; onDone: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [subject, setSubject] = useState('');
    const [body, setBody] = useState('');
    const [itemType, setItemType] = useState('racunalo');
    const [departmentId, setDepartmentId] = useState('');
    const [depts, setDepts] = useState<{ id: number; name: string }[] | null>(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    useEffect(() => {
        // poslovnice koje smijem zadati (mirror PaymentTraffic /branches scope)
        api<{ branches: { id: number; name: string }[] }>('/payment-traffic/branches', token)
            .then((r) => setDepts(r.branches))
            .catch(() => setDepts([]));
    }, [token]);

    const submit = async () => {
        if (!subject.trim()) { setErr(t('Predmet je obavezan.', 'Subject is required.')); return; }
        setBusy(true);
        setErr(null);
        try {
            const b: Record<string, unknown> = { subject: subject.trim(), item_type: itemType };
            if (body.trim()) b.body = body.trim();
            if (departmentId) b.department_id = Number(departmentId);
            await mutate(basePath, token, 'POST', b);
            onDone();
        } catch (e) {
            setErr(String((e as Error)?.message ?? e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div style={{ border: '1px dashed #d8cdb2', borderRadius: 10, padding: 14, marginBottom: 14, background: '#fffdf6' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
                <label style={{ fontSize: '0.78rem', color: BROWN }}>{t('Predmet', 'Subject')}
                    <input style={inp} value={subject} onChange={(e) => setSubject(e.target.value)} />
                </label>
                <label style={{ fontSize: '0.78rem', color: BROWN }}>{t('Vrsta', 'Item type')}
                    <select style={inp} value={itemType} onChange={(e) => setItemType(e.target.value)}>
                        {ITEM_TYPES.map((o) => <option key={o.value} value={o.value}>{locale === 'en' ? o.en : o.hr}</option>)}
                    </select>
                </label>
                <label style={{ fontSize: '0.78rem', color: BROWN }}>{t('Poslovnica', 'Branch')}
                    <select style={inp} value={departmentId} onChange={(e) => setDepartmentId(e.target.value)}>
                        <option value="">{t('— bez —', '— none —')}</option>
                        {(depts ?? []).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </select>
                </label>
            </div>
            <label style={{ fontSize: '0.78rem', color: BROWN, display: 'block', marginTop: 10 }}>{t('Opis', 'Description')}
                <textarea style={{ ...inp, minHeight: 70 }} value={body} onChange={(e) => setBody(e.target.value)} />
            </label>
            {err ? <div style={{ color: '#b14040', marginTop: 8 }}>{err}</div> : null}
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button type="button" style={tabBtn(true)} disabled={busy} onClick={submit}>{busy ? t('Slanje…', 'Submitting…') : t('Otvori tiket', 'Open ticket')}</button>
                <button type="button" style={tabBtn(false)} disabled={busy} onClick={onDone}>{t('Odustani', 'Cancel')}</button>
            </div>
        </div>
    );
}

function TicketDetail({ ticket, token, locale, canManage, basePath, onClose, onChanged }: { ticket: TicketRow; token: string; locale: Locale; canManage: boolean; basePath: string; onClose: () => void; onChanged: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [comments, setComments] = useState<Comment[] | null>(null);
    const [newComment, setNewComment] = useState('');
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState<string | null>(null);
    const [status, setStatus] = useState(ticket.status);
    const [priority, setPriority] = useState(ticket.priority);

    const loadComments = useCallback(() => {
        api<{ comments: Comment[] }>(`${basePath}/${ticket.id}/comments`, token)
            .then((r) => setComments(r.comments)).catch(() => setComments([]));
    }, [ticket.id, token]);
    useEffect(loadComments, [loadComments]);

    const addComment = async () => {
        if (!newComment.trim()) return;
        setBusy(true);
        setErr(null);
        try {
            const r = await mutate<{ comments: Comment[] }>(`${basePath}/${ticket.id}/comment`, token, 'POST', { body: newComment.trim() });
            setComments(r.comments ?? []);
            setNewComment('');
            onChanged();
        } catch (e) {
            setErr(String((e as Error)?.message ?? e));
        } finally {
            setBusy(false);
        }
    };

    const saveMeta = async (patch: Record<string, unknown>) => {
        setBusy(true);
        setErr(null);
        try {
            await mutate(`${basePath}/${ticket.id}`, token, 'PATCH', patch);
            onChanged();
        } catch (e) {
            setErr(String((e as Error)?.message ?? e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(40,30,10,0.45)', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: '40px 16px', zIndex: 9000, overflowY: 'auto' }} onClick={onClose}>
            <div style={{ ...card, maxWidth: 640, width: '100%' }} onClick={(e) => e.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                    <div>
                        <h3 style={{ margin: 0, color: BROWN }}>{ticket.subject}</h3>
                        <div className="muted" style={{ fontSize: '0.76rem' }}>{ticket.public_id} · {lookup(ITEM_TYPES, ticket.item_type, locale)} · {ticket.department_name ?? '—'}</div>
                    </div>
                    <button type="button" style={{ ...tabBtn(false), padding: '4px 10px' }} onClick={onClose}>✕</button>
                </div>

                {ticket.body ? <p style={{ fontSize: '0.88rem', whiteSpace: 'pre-wrap', background: '#fffdf6', padding: 10, borderRadius: 8 }}>{ticket.body}</p> : null}

                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', margin: '12px 0' }}>
                    <label style={{ fontSize: '0.76rem', color: BROWN }}>{t('Status', 'Status')}
                        <select style={{ ...inp, width: 160 }} value={status} disabled={!canManage || busy} onChange={(e) => { setStatus(e.target.value); saveMeta({ status: e.target.value }); }}>
                            {STATUSES.map((o) => <option key={o.value} value={o.value}>{locale === 'en' ? o.en : o.hr}</option>)}
                        </select>
                    </label>
                    <label style={{ fontSize: '0.76rem', color: BROWN }}>{t('Prioritet', 'Priority')}
                        <select style={{ ...inp, width: 160 }} value={priority} disabled={!canManage || busy} onChange={(e) => { setPriority(e.target.value); saveMeta({ priority: e.target.value }); }}>
                            {PRIORITIES.map((o) => <option key={o.value} value={o.value}>{locale === 'en' ? o.en : o.hr}</option>)}
                        </select>
                    </label>
                    {ticket.assigned_to_name ? <div style={{ fontSize: '0.76rem', color: BROWN, alignSelf: 'flex-end' }}>{t('Dodijeljen:', 'Assignee:')} <strong>{ticket.assigned_to_name}</strong></div> : null}
                </div>
                {!canManage ? <div className="muted" style={{ fontSize: '0.74rem' }}>{t('Status/prioritet mijenja HR manager.', 'Status/priority changed by HR manager.')}</div> : null}

                <hr style={{ border: 'none', borderTop: '1px solid #efe6d2', margin: '14px 0' }} />
                <strong style={{ color: BROWN, fontSize: '0.9rem' }}>{t('Komentari', 'Comments')}</strong>
                <div style={{ margin: '8px 0' }}>
                    {comments === null ? <div className="muted">{t('Učitavanje…', 'Loading…')}</div>
                        : comments.length === 0 ? <div className="muted" style={{ fontSize: '0.82rem' }}>{t('Još nema komentara.', 'No comments yet.')}</div>
                        : comments.map((c) => (
                            <div key={c.id} style={{ background: '#fffdf6', borderRadius: 8, padding: 8, marginBottom: 6 }}>
                                <div style={{ fontSize: '0.74rem', color: GOLD, fontWeight: 600 }}>{c.user_name ?? '—'} · {formatDateTime(c.created_at, locale)}</div>
                                <div style={{ fontSize: '0.86rem', whiteSpace: 'pre-wrap' }}>{c.body}</div>
                            </div>
                        ))}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    <input style={inp} value={newComment} onChange={(e) => setNewComment(e.target.value)} placeholder={t('Napiši komentar…', 'Write a comment…')} onKeyDown={(e) => { if (e.key === 'Enter') addComment(); }} />
                    <button type="button" style={tabBtn(true)} disabled={busy || !newComment.trim()} onClick={addComment}>{t('Pošalji', 'Send')}</button>
                </div>
                {err ? <div style={{ color: '#b14040', marginTop: 8 }}>{err}</div> : null}
            </div>
        </div>
    );
}
